import React, {useEffect, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faKey, faLock, faShieldAlt, faTimes,} from "@fortawesome/free-solid-svg-icons";
import useScrollLock from "../../hooks/useScrollLock";
import "../../styles/components/passcode-modal.css";

export default function PasscodeVerificationModal({
  serverPasscode = "247287",
  assetName = "Ellams GPS_LOCK",
  onVerified,
  onClose,
}) {
  useScrollLock();

  const [passcodeDigits, setPasscodeDigits] = useState(new Array(6).fill(""));
  const [error, setError] = useState(false); // Simplified error state
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);

  // Auto-focus first input
  useEffect(() => {
    if (inputRefs.current[0]) inputRefs.current[0].focus();
  }, []);

  const handleDigitChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;
    const char = value.slice(-1);

    const newDigits = [...passcodeDigits];
    newDigits[index] = char;
    setPasscodeDigits(newDigits);
    setError(false);

    // Auto-advance
    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check complete
    if (index === 5 && char) {
      const fullCode = [...newDigits.slice(0, 5), char].join("");
      if (fullCode.length === 6)
        setTimeout(() => verifyPasscode(fullCode), 200);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (passcodeDigits[index]) {
        const newDigits = [...passcodeDigits];
        newDigits[index] = "";
        setPasscodeDigits(newDigits);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newDigits = [...passcodeDigits];
        newDigits[index - 1] = "";
        setPasscodeDigits(newDigits);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const data = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(data)) {
      setPasscodeDigits(data.split(""));
      inputRefs.current[5]?.focus();
      setTimeout(() => verifyPasscode(data), 300);
    }
  };

  const verifyPasscode = (code) => {
    setIsVerifying(true);
    setTimeout(() => {
      if (String(code) === String(serverPasscode)) {
        if (onVerified) onVerified();
      } else {
        setError(true);
        setIsVerifying(false);
      }
    }, 600);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="passcode-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button className="modal-close-icon-btn" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {/* Icon & Header */}
        <div className="modal-icon-container">
          <div className="icon-circle">
            <FontAwesomeIcon icon={faShieldAlt} />
          </div>
        </div>
        <div className="modal-header-text">
          <h3>Security Verification</h3>
          <p>
            Enter the 6-digit code to unlock <strong>{assetName}</strong>
          </p>
        </div>

        {/* Server Code Display */}
        <div className="server-passcode-box">
          <div className="server-passcode-title">
            <FontAwesomeIcon icon={faKey} /> Required Code
          </div>
          <div className="server-passcode-value">{serverPasscode}</div>
          <div className="server-passcode-hint">Enter this code below</div>
        </div>

        {/* Inputs Grid */}
        <div className="passcode-input-container">
          <div className="passcode-input-grid">
            {passcodeDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={(e) => e.target.select()}
                className={`passcode-digit ${error ? "error" : ""} ${
                  digit ? "filled" : ""
                }`}
                disabled={isVerifying}
              />
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="modal-actions">
          <button
            className="btn-text-secondary"
            onClick={() => setPasscodeDigits(Array(6).fill(""))}
          >
            Clear
          </button>
          <button
            className="btn-primary-verify"
            onClick={() => verifyPasscode(passcodeDigits.join(""))}
            disabled={isVerifying || passcodeDigits.join("").length !== 6}
          >
            {isVerifying ? (
              <span>
                <FontAwesomeIcon icon={faLock} spin /> Verifying...
              </span>
            ) : (
              <span>
                Verify & Unlock <FontAwesomeIcon icon={faCheck} />
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
